import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Aufklappmenü (Glas stark) an einem Auslöser. Wird in document.body gerendert,
 * damit es nicht von abgerundeten/überlaufenden Karten abgeschnitten wird.
 * Schließt bei Klick außerhalb, Escape, Scrollen und Größenänderung.
 */
export interface PopoverProps {
  /** Auslöser – bekommt onClick/aria-expanded über die Render-Funktion. */
  ausloeser: (p: { offen: boolean; umschalten: () => void; ref: React.Ref<HTMLButtonElement> }) => React.ReactNode;
  children: (schliessen: () => void) => React.ReactNode;
  ausrichtung?: 'links' | 'rechts';
  breite?: number;
  label: string;
}

export function Popover({ ausloeser, children, ausrichtung = 'rechts', breite = 240, label }: PopoverProps) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const schliessen = useCallback(() => setOffen(false), []);

  useLayoutEffect(() => {
    if (!offen || !knopf.current) return;
    const r = knopf.current.getBoundingClientRect();
    const links = ausrichtung === 'rechts' ? r.right - breite : r.left;
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(links, window.innerWidth - breite - 8)) });
  }, [offen, ausrichtung, breite]);

  useEffect(() => {
    if (!offen) return;
    const aussen = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || knopf.current?.contains(t)) return;
      setOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOffen(false); knopf.current?.focus(); } };
    const weg = () => setOffen(false);
    document.addEventListener('mousedown', aussen);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', weg, true);
    window.addEventListener('resize', weg);
    return () => {
      document.removeEventListener('mousedown', aussen);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', weg, true);
      window.removeEventListener('resize', weg);
    };
  }, [offen]);

  return (
    <>
      {ausloeser({ offen, umschalten: () => setOffen(o => !o), ref: knopf })}
      {offen && pos && createPortal(
        <div
          ref={panel}
          role="menu"
          aria-label={label}
          className="immo-glas-stark immo-side-in flex flex-col"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: breite, zIndex: 400, borderRadius: 16, padding: 6,
            fontFamily: "'Manrope', -apple-system, sans-serif", color: 'var(--ink)', maxHeight: '60vh', overflowY: 'auto',
          }}
        >
          {children(schliessen)}
        </div>,
        document.body,
      )}
    </>
  );
}

/** Eintrag im Popover. */
export function MenueEintrag({ children, onClick, aktiv = false, gefahr = false, disabled = false }: {
  children: React.ReactNode;
  onClick: () => void;
  aktiv?: boolean;
  gefahr?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
      style={{
        gap: 8, padding: '8px 10px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: aktiv ? 700 : 600,
        background: aktiv ? 'var(--acc-soft)' : 'transparent', color: gefahr ? 'var(--bad)' : aktiv ? 'var(--acc)' : 'var(--ink)',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}

/** Zwischenüberschrift im Popover. */
export function MenueTitel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ padding: '8px 10px 4px', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
      {children}
    </span>
  );
}
